// components/ResizableLayouts/ResizableAutoRows.jsx
import React, {
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
  forwardRef,
  useImperativeHandle,
  useEffect,
} from "react";

/**
 * Minimal, reliable row resizer (vertical) with dynamic child-count safety.
 */
const ResizableAutoRows = forwardRef(function ResizableAutoRows(
  {
    children,
    minPx = 100,
    maxPx = null,
    gutter = 8,
    fixedPx: fixedPxProp,
    autoUnlockOnOverflow = false,
    className = "",
    onHeightsChange,
    classPropName
  },
  ref
) {
  const rows = useMemo(() => {
    const arr = Array.isArray(children) ? children : [children];
    return arr.filter((c) => c !== null && c !== undefined && c !== false);
  }, [children]);
  const n = rows.length;

  const toArrSized = (v, fallback) => {
    if (Array.isArray(v)) {
      const out = v.slice(0, n);
      while (out.length < n) out.push(fallback);
      return out;
    }
    return Array(n).fill(v ?? fallback);
  };

  const minArr = useMemo(() => toArrSized(minPx, 100), [minPx, n]);
  const maxArr = useMemo(() => toArrSized(maxPx, null), [maxPx, n]);

  const [fixedPx, setFixedPx] = useState(() =>
    fixedPxProp && fixedPxProp.length === n
      ? fixedPxProp.slice()
      : Array(n).fill(null)
  );
  const [draggingIdx, setDraggingIdx] = useState(null);
  const [animMask, setAnimMask] = useState(() => Array(n).fill(false));
  const animTimers = useRef([]);

  // Refs bucket with dynamic size
  const containerRef = useRef(null);
  const rowRefs = useRef([]);
  const ensureRefCount = () => {
    if (rowRefs.current.length !== n) {
      const prev = rowRefs.current;
      const next = Array(n);
      for (let i = 0; i < n; i++) next[i] = prev[i] ?? React.createRef();
      rowRefs.current = next;
    }
  };
  ensureRefCount();

  // Pad/trim state arrays when n changes
  useEffect(() => {
    setFixedPx((prev) => {
      const next = prev.slice(0, n);
      while (next.length < n) next.push(null);
      return next;
    });
    setAnimMask((prev) => {
      const next = prev.slice(0, n);
      while (next.length < n) next.push(false);
      return next;
    });
    animTimers.current = (animTimers.current || []).slice(0, n);
  }, [n]);

  const rafRef = useRef(null);

  const bumpAnim = (indices) => {
    setAnimMask((prev) => {
      const next = prev.slice();
      indices.forEach((i) => {
        if (i < 0 || i >= n) return;
        next[i] = true;
        if (animTimers.current[i]) clearTimeout(animTimers.current[i]);
        animTimers.current[i] = setTimeout(() => {
          setAnimMask((p2) => {
            const n2 = p2.slice();
            n2[i] = false;
            return n2;
          });
        }, 220);
      });
      return next;
    });
  };

const applyLayout = () => {
  for (let i = 0; i < n; i++) {
    const el = rowRefs.current[i]?.current;
    if (!el) continue;
    const isFixed = fixedPx[i] != null;
    const basis = isFixed ? clamp(fixedPx[i], minArr[i], maxArr[i]) : null;
    el.style.transition =
      draggingIdx === i || animMask[i] ? "flex-basis 160ms ease" : "none";
    el.style.minHeight = "0px";
    el.style.maxHeight = maxArr[i] != null ? `${maxArr[i]}px` : "";
    if (isFixed) el.style.flex = `0 0 ${Math.max(0, basis)}px`;
    else el.style.flex = "1 1 auto";
  }
};

   useLayoutEffect(() => {
     applyLayout();
     // eslint-disable-next-line react-hooks/exhaustive-deps
   }, [fixedPx, draggingIdx, animMask, gutter, n, minArr, maxArr]);

useEffect(
  () => () => {
    (animTimers.current || []).forEach((t) => t && clearTimeout(t));
  },
  []
);

  // Dragging
  const dragRef = useRef(null);

  const onHandleDown = (i, e) => {
    e.preventDefault();
    e.stopPropagation();

    const rowEl = rowRefs.current[i]?.current;
    if (!rowEl) return;
    const rect = rowEl.getBoundingClientRect();

    dragRef.current = { index: i, startY: e.clientY, startHeight: rect.height };

    setDraggingIdx(i);
    e.currentTarget.setPointerCapture?.(e.pointerId);
    window.addEventListener("pointermove", onMove, { passive: false });
    window.addEventListener("pointerup", onUp, { passive: false });
    window.addEventListener("pointercancel", onUp, { passive: false });
    document.body.style.userSelect = "none";
  };

  const onMove = (e) => {
    e.preventDefault();
    const d = dragRef.current;
    if (!d) return;
    const i = d.index;
    const dy = e.clientY - d.startY;

    let h = d.startHeight + dy;
    h = clamp(h, minArr[i], maxArr[i]);

    const el = rowRefs.current[i]?.current;
    if (el) {
      el.style.transition = "flex-basis 0ms";
      el.style.flex = `0 0 ${Math.max(0, h)}px`;
    }
  };

  const onUp = () => {
    const d = dragRef.current;
    dragRef.current = null;

    window.removeEventListener("pointermove", onMove);
    window.removeEventListener("pointerup", onUp);
    window.removeEventListener("pointercancel", onUp);
    document.body.style.userSelect = "";
    setDraggingIdx(null);

    if (!d) return;
    const i = d.index;
    const el = rowRefs.current[i]?.current;
    if (!el) return;

    const finalHeight = el.getBoundingClientRect().height;
    setFixedPx((prev) => {
      const next = prev.slice();
      next[i] = clamp(finalHeight, minArr[i], maxArr[i]);
      return next;
    });

    if (onHeightsChange) {
      requestAnimationFrame(() => {
        const total = getColUsableHeight(containerRef.current, gutter, n) || 1;
        const px = rowRefs.current.map(
          (r) => r?.current?.getBoundingClientRect().height || 0
        );
        const pct = px.map((h) => (h / total) * 100);
        onHeightsChange({ px, pct });
      });
    }
  };

  // Optional: auto-unlock on overflow
  useEffect(() => {
    if (!autoUnlockOnOverflow) return;

    const observers = [];
    for (let i = 0; i < n; i++) {
      const el = rowRefs.current[i]?.current;
      if (!el) continue;

      const ro = new ResizeObserver(() => {
        if (fixedPx[i] == null) return;
        const overflow = el.scrollHeight > el.clientHeight + 1;
        if (overflow) {
          bumpAnim([i]);
          setFixedPx((prev) => {
            if (prev[i] == null) return prev;
            const next = prev.slice();
            next[i] = null;
            return next;
          });
        }
      });
      ro.observe(el);
      observers.push(ro);
    }
    return () => observers.forEach((o) => o.disconnect());
  }, [autoUnlockOnOverflow, fixedPx, n]);

  // Imperative API
  useImperativeHandle(ref, () => ({
    setHeight(i, px) {
      if (!Number.isInteger(i) || i < 0 || i >= n) return false;
      bumpAnim([i]);
      setFixedPx((prev) => {
        const next = prev.slice();
        next[i] = clamp(px, minArr[i], maxArr[i]);
        return next;
      });
      requestAnimationFrame(() => {
        if (onHeightsChange) {
          const total =
            getColUsableHeight(containerRef.current, gutter, n) || 1;
          const hs = rowRefs.current.map(
            (r) => r?.current?.getBoundingClientRect().height || 0
          );
          const pct = hs.map((h) => (h / total) * 100);
          onHeightsChange({ px: hs, pct });
        }
      });
      return true;
    },
    reset(i) {
      if (!Number.isInteger(i) || i < 0 || i >= n) return false;
      bumpAnim([i]);
      setFixedPx((prev) => {
        const next = prev.slice();
        next[i] = null;
        return next;
      });
      return true;
    },
    resetAll() {
      bumpAnim([...Array(n).keys()]);
      setFixedPx(Array(n).fill(null));
      return true;
    },
    getHeights() {
      const total = getColUsableHeight(containerRef.current, gutter, n) || 1;
      const px = rowRefs.current.map(
        (r) => r?.current?.getBoundingClientRect().height || 0
      );
      const pct = px.map((h) => (h / total) * 100);
      return { px, pct, fixedPx: fixedPx.slice() };
    },
  }));

  return (
    <div
      ref={containerRef}
      className={className}
      style={{
        display: "flex",
        flexDirection: "column",
        alignItems: "stretch",
        height: "100%",
        width: "100%",
        gap: 0,
        isolation: "isolate",
      }}
    >
      {rows.map((child, i) => (
        <React.Fragment key={i}>
          <div
            ref={rowRefs.current[i]}
            onDoubleClick={() =>
              setFixedPx((prev) => {
                const next = prev.slice();
                next[i] = null;
                return next;
              })
            }
            style={{
              overflow: "hidden",
              display: "flex",
              flexDirection: "column",
            // Make first paint correct (no size jump)
      minHeight: "0px",
      maxHeight: maxArr[i] != null ? `${maxArr[i]}px` : undefined,
      flex:
        fixedPx[i] != null
          ? `0 0 ${Math.max(
              0,
              clamp(fixedPx[i], minArr[i], maxArr[i])
            )}px`
          : "1 1 auto",
            }}
          >
            {child}
          </div>

          {i < n - 1 && (
            <div
              style={{
                flex: "0 0 auto",
                height: `${gutter}px`,
                position: "relative",
                zIndex: 4,
                background: "transparent",
                touchAction: "none",
                pointerEvents: "none", // spacer doesn't eat events
              }}
            >
              <div
                onPointerDown={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  onHandleDown(i, e);
                }}
                role="separator"
                aria-orientation="horizontal"
                tabIndex={0}
                title="Drag to resize"
                style={{
                  position: "absolute",
                  left: 6,
                  right: 6,
                  top: 0,
                  bottom: 0,
                  cursor: "row-resize",
                  pointerEvents: "auto",
                }}
              />
              <div
                // style={{
                //   position: "absolute",
                //   left: 6,
                //   right: 6,
                //   top: "50%",
                //   transform: "translateY(-50%)",
                //   height: "2px",
                //   borderRadius: 2,
                //   opacity: 0.25,
                //   background: "currentColor",
                //   pointerEvents: "none",
                // }}
                style={{
                  position: "absolute",
                  top: "50%",
                  left: "50%",
                  transform: "translate(-50%, -50%)",
                  height: "2px",
                  width: "20%",
                  borderRadius: 2,
                  opacity: 0.25,
                  background: "currentColor",
                  pointerEvents: "none",
                }}
              />
            </div>
          )}
        </React.Fragment>
      ))}
    </div>
  );
});

export default ResizableAutoRows;

// ---- utils ----
function clamp(value, min, max) {
  const lo = min ?? -Infinity;
  const hi = max ?? +Infinity;
  return Math.max(lo, Math.min(hi, value));
}
function getColUsableHeight(container, gutter, n) {
  if (!container) return 0;
  return container.clientHeight - gutter * (n - 1);
}
